"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/store";
import { getSettings, type Settings } from "@/lib/settings-api";
import ProfileSection from "@/components/settings/ProfileSection";
import ToneSection from "@/components/settings/ToneSection";
import AIMemorySection from "@/components/settings/AIMemorySection";
import ThemeSection from "@/components/settings/ThemeSection";
import AutonomySection from "@/components/settings/AutonomySection";
import WorkingHoursSection from "@/components/settings/WorkingHoursSection";
import QuietHoursSection from "@/components/settings/QuietHoursSection";
import GoogleSection from "@/components/settings/GoogleSection";
import PushSection from "@/components/settings/PushSection";
import NotionSection from "@/components/settings/NotionSection";

export default function SettingsPage() {
  const { user, loading, logout } = useAuth();
  const router = useRouter();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) {
      router.push("/sign-in");
      return;
    }
    if (user) {
      getSettings()
        .then(setSettings)
        .catch((e) => setError(e.message));
    }
  }, [loading, user, router]);

  const handleSignOut = async () => {
    await logout();
    router.push("/sign-in");
  };

  if (loading || !user) {
    return (
      <div className="h-[calc(100vh-52px)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-forest border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24 max-w-lg mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-forest ">Settings</h1>
          <p className="text-sm text-muted mt-0.5">Customise your experience</p>
        </div>
        <button
          onClick={() => router.push("/")}
          className="text-sm text-forest  font-medium hover:underline"
        >
          Done
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-amber/10 border border-amber/30 rounded-xl text-sm text-amber">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <ProfileSection settings={settings} onUpdate={setSettings} />
        <ToneSection settings={settings} onUpdate={setSettings} />
        <AIMemorySection />
        <ThemeSection settings={settings} onUpdate={setSettings} />
        <AutonomySection settings={settings} onUpdate={setSettings} />
        <WorkingHoursSection settings={settings} onUpdate={setSettings} />
        <QuietHoursSection settings={settings} onUpdate={setSettings} />
        <GoogleSection settings={settings} onUpdate={setSettings} />
        <PushSection />
        <NotionSection />

        {/* Sign Out */}
        <div className="pt-4">
          <button
            onClick={handleSignOut}
            className="w-full py-3 bg-rose/10 text-rose rounded-xl font-medium text-sm
                       hover:bg-rose/15 transition-colors border border-rose/30"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
